import type { SubscriptionView } from '../api/generated/models/SubscriptionView';
import { CONTENT_MASTER_CORE_FIELD_KEYS } from '../store/slices/contentMasterSlice';
import { helper } from './helper';

export type CmEntitlements = {
  editWawiAttributes: boolean;
  bulkGenerationIncluded: boolean;
};

const CONTENT_MASTER_PLUGIN = 'content_master';

/** Add-on subscription plugin that unlocks the marketplace-listing feature. */
export const CONTENT_MASTER_MARKETPLACE_ADDON_PLUGIN =
  'content_master_addon_marketplace_listing';

const TRUTHY_OPTION_VALUES = new Set(['true', '1', 'yes']);

const parseSubscriptionOptionBool = (
  options: SubscriptionView['options'],
  slug: string
): boolean => {
  const raw = (options ?? []).find((o) => o.slug === slug)?.value ?? '';
  return TRUTHY_OPTION_VALUES.has(String(raw).trim().toLowerCase());
};

export function parseCmEntitlements(subscription: SubscriptionView | null): CmEntitlements {
  const opts = subscription?.options ?? [];
  return {
    editWawiAttributes: parseSubscriptionOptionBool(opts, 'edit_wawi_attributes'),
    bulkGenerationIncluded: parseSubscriptionOptionBool(opts, 'bulk_generation_included'),
  };
};

export function getActiveContentMasterSubscription(
  subscriptions: SubscriptionView[]
): SubscriptionView | null {
  return (
    subscriptions.find(
      (s) => s.pluginName === CONTENT_MASTER_PLUGIN && helper.isSubscriptionActive(s)
    ) ?? null
  );
}

/**
 * True when the user has an active `content_master_addon_marketplace_listing`
 * subscription, OR an active trial `content_master` subscription (the trial
 * product includes the Marketplace Listing add-on, `is_trial` option).
 * Marketplace-listing access additionally requires an active base
 * `content_master` subscription - the backend checks BOTH.
 */
export function hasActiveMarketplaceListingAddon(
  subscriptions: SubscriptionView[]
): boolean {
  const addonActive = subscriptions.some(
    (s) =>
      s.pluginName === CONTENT_MASTER_MARKETPLACE_ADDON_PLUGIN &&
      helper.isSubscriptionActive(s)
  );
  if (addonActive) {
    return true;
  }
  const activeSubscription = getActiveContentMasterSubscription(subscriptions);
  return parseSubscriptionOptionBool(activeSubscription?.options, 'is_trial');
}

export function resolveContentMasterEntitlements(
  subscriptions: SubscriptionView[]
): CmEntitlements {
  return parseCmEntitlements(getActiveContentMasterSubscription(subscriptions));
}

export function isContentMasterCoreGenerateField(fieldApiName: string): boolean {
  return (CONTENT_MASTER_CORE_FIELD_KEYS as readonly string[]).includes(fieldApiName);
}

export function filterGenerateFieldsForEntitlements(
  fields: string[],
  entitlements: CmEntitlements
): string[] {
  if (entitlements.editWawiAttributes) {
    return fields;
  }
  return fields.filter(isContentMasterCoreGenerateField);
}
